// src/components/widgets/ConstructionPhotoWidget.jsx

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getPhotoList } from "../../api/const";
import WidgetCard from "./WidgetCard";

export default function ConstPhotoWidget() {
  const projectId = localStorage.getItem("projectId");
  const [photos, setPhotos] = useState([]);
  const scrollRef = useRef(null);

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const navigate = useNavigate();

  // ▶ 최신 10개 로드
  useEffect(() => {
    const loadPhotos = async () => {
      try {
        const res = await getPhotoList(projectId);
        const items = Array.isArray(res) ? res : res?.results || [];

        const normalized = items.slice(0, 10).map((p) => ({
          id: p.id,
          file_url: p.file_url || p.file,
          description: p.description || p.title || "",
          date: p.date || p.shot_date || "",
        }));

        setPhotos(normalized);
      } catch (e) {
        console.error("시공사진 로드 실패", e);
      }
    };

    if (projectId) loadPhotos();
  }, [projectId]);

  // ▶ 스크롤 위치 확인
  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setAtStart(scrollLeft <= 0);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [photos.length]);

  const moveLeft = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollLeft - el.clientWidth * 0.9, behavior: "smooth" });
  };

  const moveRight = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollLeft + el.clientWidth * 0.9, behavior: "smooth" });
  };

  return (
    <WidgetCard title="시공 사진">
      {/* ────────────── 헤더 오른쪽 정보 ────────────── */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-[11px] text-slate-400">
          최신 {photos.length}개
        </span>

        <button
          onClick={() => navigate(`/project/${projectId}/const/photo`)}
          className="text-xs text-blue-600 hover:underline"
        >
          더보기 →
        </button>
      </div>

      {/* ────────────── 사진 슬라이더 ────────────── */}
      <div className="relative w-full h-40 md:h-48">

        {/* 좌버튼 */}
        <button
          onClick={moveLeft}
          disabled={atStart}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 
            bg-white/90 border rounded-full p-1.5 shadow 
            hover:bg-slate-50 disabled:opacity-30"
        >
          <ChevronLeft size={20} />
        </button>

        {/* 우버튼 */}
        <button
          onClick={moveRight}
          disabled={atEnd}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 
            bg-white/90 border rounded-full p-1.5 shadow 
            hover:bg-slate-50 disabled:opacity-30"
        >
          <ChevronRight size={20} />
        </button>

        {/* 스크롤 영역 */}
        <div
          ref={scrollRef}
          className="w-full h-full overflow-hidden rounded-lg"
        >
          <div className="flex gap-3 h-full w-max overflow-x-auto">
            {photos.map((p) => (
              <div
                key={p.id}
                onClick={() => navigate(`/project/${projectId}/const/photo/view/${p.id}`)}
                className="flex-shrink-0 w-40 sm:w-52 md:w-60 
                  rounded-lg overflow-hidden border bg-white shadow-sm cursor-pointer
                  active:scale-[0.98] transition-transform"
              >
                {/* 이미지 */}
                <div className="h-28 md:h-32 bg-gray-100 overflow-hidden flex items-center justify-center">
                  <img
                    src={p.file_url}
                    alt={p.description}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* 제목 + 날짜 */}
                <div className="p-2">
                  <div className="text-sm font-semibold text-slate-700 truncate">
                    {p.description || "제목 없음"}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 truncate">
                    {p.date || "-"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </WidgetCard>
  );
}
